import { Injectable, inject } from '@angular/core'
import { ApiClient, ApiClientFactory, serializeHttpParams } from '@ftr/api-shared'
import { PageDetails, PagedResult } from '@ftr/contracts/api/core'
import {
  AddUsersToCourtSystemGroupsBody,
  CourtSystem,
  CourtSystemOverviewResponse,
  CourtSystemUser,
  CourtSystemWithMetadata,
  CreateCourtSystemBody,
  PatchCourtSystemBody,
  SetCourtUserWithGroupsBody,
} from '@ftr/contracts/api/court-system'
import { RecordingLicense } from '@ftr/contracts/api/recording-license'
import { CourtSystemIdQuery } from '@ftr/contracts/api/shared'
import { SttLicense } from '@ftr/contracts/api/stt-license'
import { ListUsersForCourtSystemQuery, User, UserType } from '@ftr/contracts/api/user'
import { UserGetForCourtSystemParams } from '@ftr/contracts/read'
import { sortAlphabeticallyByProps } from '@ftr/contracts/shared/ArrayUtils'
import { CourtSystemAddOnConfig } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, createAndAssign, mapData } from '@ftr/foundation'
import { GetCourtSystemService } from './get-court-system.service'

const USER_PAGE_SIZE = 100

@Injectable({
  providedIn: 'root',
})
export class CourtSystemService {
  private getCourtSystemService = inject(GetCourtSystemService)

  private readonly courtSystemApiClient: ApiClient
  private readonly userApiClient: ApiClient
  private readonly sttLicenseApiClient: ApiClient
  private readonly recordingLicenseApiClient: ApiClient

  constructor() {
    const apiClientFactory = inject(ApiClientFactory)
    this.courtSystemApiClient = apiClientFactory.build('/court-system')
    this.userApiClient = apiClientFactory.build('/user')
    this.sttLicenseApiClient = apiClientFactory.build('stt-license')
    this.recordingLicenseApiClient = apiClientFactory.build('recording-license')
  }

  getAllCourtSystems(): ApiResult<CourtSystemWithMetadata[]> {
    return this.courtSystemApiClient.get<CourtSystemWithMetadata[]>({
      path: '/all',
    })
  }

  getCourtSystem<T extends CourtSystem>(courtSystemId: string, includeAddonCounts: boolean = false): ApiResult<T> {
    return this.getCourtSystemService.getCourtSystem(courtSystemId, includeAddonCounts) as ApiResult<T>
  }

  getCourtSystemOverview(courtSystemId: Uuid): ApiResult<CourtSystemOverviewResponse> {
    return this.courtSystemApiClient.get<CourtSystemOverviewResponse>({
      path: `/${courtSystemId}/overview`,
    })
  }

  getUser(courtSystemId: string, userId: string): ApiResult<CourtSystemUser> {
    return this.userApiClient.get({
      path: '/forCourtSystem',
      params: serializeHttpParams(createAndAssign(UserGetForCourtSystemParams, { courtSystemId, userId })),
    })
  }

  getUserById(courtSystemId: string, userId: string): ApiResult<User> {
    return this.userApiClient.get({
      path: '/getById',
      params: serializeHttpParams(createAndAssign(UserGetForCourtSystemParams, { courtSystemId, userId })),
    })
  }

  getUsers(
    courtSystemId: string,
    page: number,
    searchTerms?: string,
    userGroupId?: Uuid,
  ): ApiResult<PagedResult<CourtSystemUser>> {
    return this.courtSystemApiClient
      .get<PagedResult<CourtSystemUser>>({
        path: `/${courtSystemId}/users`,
        params: serializeHttpParams(
          createAndAssign(ListUsersForCourtSystemQuery, {
            searchTerms,
            userGroupId,
            page: new PageDetails(page, USER_PAGE_SIZE),
          }),
        ),
      })
      .pipe(mapData(data => mapPagedUsers(data)))
  }

  updateUserGroups(courtSystemId: string, body: SetCourtUserWithGroupsBody): ApiResult<CourtSystemUser> {
    return this.courtSystemApiClient.put({
      path: `/${courtSystemId}/user`,
      body,
    })
  }

  addUsersToGroups(courtSystemId: Uuid, body: AddUsersToCourtSystemGroupsBody): ApiResult<CourtSystemUser[]> {
    return this.courtSystemApiClient.put({
      path: `/${courtSystemId}/add-users`,
      body,
    })
  }

  create(createCourtSystemBody: CreateCourtSystemBody): ApiResult<CourtSystem> {
    return this.courtSystemApiClient.post({ body: createCourtSystemBody })
  }

  patch(courtSystemId: Uuid, body: PatchCourtSystemBody): ApiResult<CourtSystem> {
    return this.courtSystemApiClient.patch({
      path: `/${courtSystemId}/`,
      body,
    })
  }

  getUserTypesWithCount(courtSystemId: Uuid): ApiResult<UserType[]> {
    return this.courtSystemApiClient.get({
      path: `/${courtSystemId}/userTypesWithCount`,
    })
  }

  getAddons(courtSystemId: Uuid): ApiResult<CourtSystemAddOnConfig> {
    return this.courtSystemApiClient.get({
      path: `/${courtSystemId}/addons`,
    })
  }

  getSttLicense(courtSystemId: Uuid): ApiResult<SttLicense> {
    const params: CourtSystemIdQuery = { courtSystemId }
    return this.sttLicenseApiClient.get({
      params: serializeHttpParams(params),
      responseBodyType: SttLicense,
    })
  }

  getRecordingLicense(courtSystemId: Uuid): ApiResult<RecordingLicense> {
    const params: CourtSystemIdQuery = { courtSystemId }
    return this.recordingLicenseApiClient.get({
      params: serializeHttpParams(params),
      responseBodyType: RecordingLicense,
    })
  }
}

function mapPagedUsers(users: PagedResult<CourtSystemUser>): PagedResult<CourtSystemUser> {
  return {
    meta: users.meta,
    items: users.items.map(user => {
      return {
        ...user,
        groups: user.groups.sort(sortAlphabeticallyByProps('name')),
      }
    }),
  }
}
